import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationSection,
  CommonQuestionKeys,
  FieldType,
  KCCustomQuestionKeys,
  KCSMBLoanPrepCustomQuestionKeys,
  SMBGrantCustomQuestionKeys,
  TextInputType,
} from '@bybeam/platform-types';
import STANDARD_COMPONENTS from '../standards/index.js';
import { SpecialSectionKeys } from '../standards/types.js';
import { QUESTIONS as GRANT_QUESTIONS } from './questions/grantQuestions.js';
import { QUESTIONS as LOAN_PREP_QUESTIONS } from './questions/loanPrepQuestions.js';
import { QUESTIONS as SHARED_QUESTIONS } from './questions/sharedQuestions.js';

const SECTIONS: ApplicationSection[] = [
  {
    key: 'ownerInfo',
    name: 'Owner Information',
    overview: {
      title: 'Owner Information',
      description: '',
    },
    questionGroups: [
      {
        key: 'ownerInfo',
        name: 'Owner Information',
        questions: [
          LOAN_PREP_QUESTIONS[KCCustomQuestionKeys.COMPANY_LEGAL_NAME],
          SHARED_QUESTIONS[KCCustomQuestionKeys.DOING_BUSINESS_AS],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.OTHER_RELATED_BUSINESSES],
          LOAN_PREP_QUESTIONS[KCCustomQuestionKeys.BUSINESS_ADDRESS],
          LOAN_PREP_QUESTIONS[CommonQuestionKeys.MAILING_ADDRESS],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.PRIMARY_CONTACT_NAME],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.PRIMARY_CONTACT_TITLE],
          LOAN_PREP_QUESTIONS[CommonQuestionKeys.PHONE_NUMBER],
          LOAN_PREP_QUESTIONS[CommonQuestionKeys.EMAIL],
          LOAN_PREP_QUESTIONS[CommonQuestionKeys.ETHNICITY],
          LOAN_PREP_QUESTIONS[CommonQuestionKeys.GENDER],
          LOAN_PREP_QUESTIONS[KCCustomQuestionKeys.CREDIT_SCORE],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.OWNER_INFORMATION],
          {
            ...SHARED_QUESTIONS[KCCustomQuestionKeys.PRODUCTS_SERVICES],
            copy: { title: 'What type of products and/or services does the business provide?' },
          },
          {
            ...SHARED_QUESTIONS[KCCustomQuestionKeys.YEAR_ESTABLISHED],
            copy: {
              title: 'What month and year did the business start or formally incorporate?',
              intro: "If you can't recall the exact date, feel free to provide an estimate.",
            },
          },
          SHARED_QUESTIONS[KCCustomQuestionKeys.EMPLOYEES],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.LOAN_AMOUNT],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.BANK_OR_FINANCIAL_INSTITUTION],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.PURPOSE_OF_FINANCING],
        ],
      },
    ],
  },
  {
    key: 'financialInformation',
    name: 'Business Financial Information',
    overview: {
      title: 'Business Financial Information',
      description: '',
    },
    questionGroups: [
      {
        key: 'financialInformation',
        name: 'Business Financial Information',
        questions: [
          {
            ...GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.YEARLY_SALES],
            fields: [
              {
                type: FieldType.Typography,
                copy: 'Please note that your financial records may be requested.',
                props: { variant: 'body', alignment: 'center' },
                key: 'yearlySales.helper',
              },
              {
                type: FieldType.Text,
                inputType: TextInputType.Currency,
                key: 'yearlySales.from2023',
                copy: '2023',
              },
              {
                type: FieldType.Text,
                inputType: TextInputType.Currency,
                key: 'yearlySales.from2022',
                copy: '2022',
              },
              {
                type: FieldType.Text,
                inputType: TextInputType.Currency,
                key: 'yearlySales.from2021',
                copy: '2021',
              },
              {
                type: FieldType.Text,
                inputType: TextInputType.Currency,
                key: 'yearlySales.from2020',
                copy: '2020',
                validation: { required: false },
              },
              {
                type: FieldType.Text,
                inputType: TextInputType.Currency,
                key: 'yearlySales.from2019',
                copy: '2019',
                validation: { required: false },
              },
              {
                key: 'yearlySales.yearlySales.label',
                type: FieldType.Typography,
                copy: 'Sales or Revenue (please indicate what you provided above)',
                props: { variant: 'body', alignment: 'center' },
              },
              {
                type: FieldType.Dropdown,
                options: [
                  { label: 'Revenue', value: 'revenue' },
                  { label: 'Sales', value: 'sales' },
                ],
                key: 'yearlySales.salesOrRevenue',
                copy: 'Please choose one',
              },
            ],
          },
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.CASH_FLOW_POSITIVE],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.YTD_EXPENSES],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.PERSONAL_INCOME_AND_EXPENSES],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.BUSINESS_PAYS_INCOME],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.HAVE_ANY_OPEN_LOANS],
        ],
      },
    ],
  },
  {
    key: 'taxInformation',
    name: 'Business Tax Information',
    overview: {
      title: 'Business Tax Information',
      description: '',
    },
    questionGroups: [
      {
        key: 'taxInformation',
        name: 'Business Tax Information',
        questions: [
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.FISCAL_YEAR_FINANCIAL_INFORMATION],
          LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.SUPPORTING_DOCUMENTS],
        ],
      },
    ],
  },
  {
    key: 'businessPlanSummary',
    name: 'Business Plan Summary',
    overview: {
      title: 'Business Plan Summary',
      description: '',
    },
    questionGroups: [
      {
        key: 'businessPlanSummary',
        name: 'Business Plan Summary',
        questions: [
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.BUSINESS_DESCRIPTION],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.BUSINESS_PLAN],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.SOCIAL_IMPACT],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.PRIOR_FUNDING],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.MARKET_POTENTIAL],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.REVENUE_GOAL],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.CHALLENGES_AND_MITIGATION],
        ],
      },
    ],
  },
  mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.sections[SpecialSectionKeys.Review],
      {
        questionGroups: [
          // Drop the first question
          null,
          // Drop the first question and replace it with this one
          {
            questions: [
              null,
              LOAN_PREP_QUESTIONS[KCSMBLoanPrepCustomQuestionKeys.CAN_COMMIT_TO_STATUS_UPDATES],
              GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.ANY_CHALLENGES],
              LOAN_PREP_QUESTIONS.affirmation,
            ],
          },
        ],
      } as ApplicationSection,
    ],
    'merge',
  ),
];

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'default',
      title: 'Small Business Loan Preparation Application',
      body: `The Small Business Loan Application Prep program is a collaborative initiative between the City of Kansas City, MO, the Kauffman Foundation, the Economic Development Corporation of Kansas City, and local financial institutions, to provide loan packaging services to Kansas City, Missouri based businesses.  The program focusses on outreach to local small businesses, targeting minority owned businesses who need additional assistance with completing their loan applications to submit to our banking partner(s). 

This program is being conducted in partnership with the Prospect Business Association of Kansas City who will be assisting small business owners with the collection of financial documents, accounting support services, review of documentation, completion of client application and follow up via a business development specialist and/or accounting support services if the loan is approved.`,
    },
    {
      key: 'requirements',
      layout: 'default',
      title:
        'Before you begin, please review the program eligibility criteria carefully to ensure your business qualifies:',
      body: `
1. Must be private, for-profit business.
2. Must have been in business for at least three (3) years, i.e. established prior to January 1, 2021.
3. Must maintain an active KCMO business license (upload a copy)
4. Must maintain a principal place of business within the boundaries of KCMO.
5. Must be able to provide tax returns from previous years.
6. Must be able to provide business financials (P&L and Balance Sheet)
7. Must plan on applying for a bank loan within the next 6-12 months.      
  `,
    },
    STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: '',
        referralMessage: 'You have been referred to this program.',
        message: `Capital assistance for small businesses 

Your business may be eligible for one of various forms of business development assistance, including educational and/or financial. Apply now!`,
      },
      {
        status: 'InProgress',
        label: '',
        referralMessage: 'You have been referred to this program.',
        message:
          'You’ve made great progress so far! Keep working on your application to receive business development assistance.',
      },
      {
        status: 'AwaitingReview',
        label: 'Status: Awaiting Review',
        message:
          'Thank you for submitting your application! You will be notified if our team needs additional information and/or of any next steps. Check back here for updates.',
      },
    ],
    submission: {
      title: 'What happens next?',
      bullets: [
        {
          key: 'review',
          title: 'Application review',
          icon: 'description',
          description:
            'While we look at your application, we might send you an email to ask for more information or notify you of next steps',
        },
      ],
    },
  },
  sections: SECTIONS,
};

export default CONFIG;
